<script>
import InputComponent from '@/packages/ui/components/Input/Input.vue';
import AutoCompleteInput from '@/packages/ui/components/Select/AutoCompleteInput.vue';
import clickoutDirective from '@/packages/ui/utils/clickoutDirective';
import Tooltip from '@/packages/ui/components/Tooltip/Tooltip.vue';
import Icon from '@/packages/ui/components/Icon/Icon.vue';

export default {
  name: 'Select',
  components: {
    Icon, InputComponent, AutoCompleteInput, Tooltip,
  },
  directives: clickoutDirective,
  props: {
    id: {
      type: String,
      default: '',
    },
    option: {
      type: [String, Number, Object, Array],
      default: '',
    },
    display: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    placeholderAutocomplete: {
      type: String,
      default: '',
    },
    backendFilter: {
      type: Boolean,
      default: false,
    },
    backendPaginationLimit: {
      type: Number,
      default: 50,
    },
    surveys: {
      type: Array,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    valueId: {
      type: String,
      default: '',
    },
    valueLabel: {
      type: String,
      default: '',
    },
    displayValue: {
      type: String,
      default: '',
    },
    emitValue: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    infoMessage: {
      type: String,
      default: '',
    },
    context: {
      type: String,
      default: '',
      validator: (val) => ['warn', 'alert', 'success', ''].includes(val),
    },
    timeDebounced: {
      type: Number,
      default: 400,
    },
    dropdownDirection: {
      type: String,
      default: 'bottom',
    },
    showLeftIcon: {
      type: Boolean,
      default: false,
    },
    iconLeft: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      open: false,
      error: null,
      autocompleteLabel: '',
      filteredList: [],
    };
  },
  computed: {
    hasOptionSlot() {
      return !!this.$scopedSlots.option;
    },
    paceholderAutocompleteText() {
      return this.placeholderAutocomplete && this.backendFilter
        ? this.placeholderAutocomplete
        : '';
    },
    hasIconSlot() {
      return !!this.$scopedSlots.iconRight;
    },
    labelShowed() {
      if (this.displayValue) {
        return this.displayValue;
      } if (this.selected) {
        return this.getLabel(this.selected);
      }
      return '';
    },
    selected: {
      get() {
        if (this.emitValue) {
          if (Array.isArray(this.option) && this.multiple) {
            return this.option.map((option) => this.options.find((op) => op[this.valueId] === option));
          }
          return this.options.find((op) => op[this.valueId] === this.option);
        }
        return this.option;
      },
      set(value) {
        if (Array.isArray(this.option) && this.multiple) {
          this.updateMultipleValue(value);
        } else {
          this.updateSingleValue(value);
        }
      },
    },
    dropdownClass() {
      return this.dropdownDirection === 'top' ? '--dropdown-top' : '--dropdown-bottom';
    },
    getClasses() {
      return {
        disabled: this.disabled,
        context: this.context,
        error: this.errorMessage,
        '-display': this.display,
      };
    },
  },
  watch: {
    autocompleteLabel(newValue, oldValue) {
      if ((this.backendFilter && this.options.length >= this.backendPaginationLimit) || (this.backendFilter && newValue.length < oldValue.length)) {
        if (this.debouncedSearch) clearTimeout(this.debouncedSearch);

        this.debouncedSearch = setTimeout(() => {
          this.$emit('fetchResults', { text: newValue, surveyIds: this.surveys?.map((survey) => survey.uuid) });
        }, this.timeDebounced);
        return;
      }
      if (!newValue) {
        this.filteredList = this.options;
        return;
      }
      this.filteredList = this.options.filter((v) => {
        let valueToCompare = '';
        if (this.valueLabel) {
          valueToCompare = v[this.valueLabel];
        } else {
          valueToCompare = v;
        }
        const valueToCompareNormalized = valueToCompare
          ?.normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        const valueNormalized = newValue
          ?.normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        return valueToCompareNormalized
          ?.toLowerCase()
          .includes(valueNormalized?.toLowerCase());
      });
    },
    options() {
      this.filteredList = this.options;
    },
  },
  created() {
    this.filteredList = this.options;
  },
  methods: {
    hide() {
      this.open = false;
    },
    selectOption(option) {
      if (option.disabled) {
        return;
      }
      this.selected = option;
      if (!this.multiple) {
        this.toggleOpen();
      }
    },
    updateSingleValue(value) {
      if (this.emitValue) {
        this.$emit('change', value[this.valueId]);
      } else {
        this.$emit('change', value);
      }
    },
    updateMultipleValue(value) {
      const finalValue = this.emitValue ? value[this.valueId] : value;
      const index = this.option.findIndex((val) => {
        if (val) {
          return val === finalValue;
        }
        return false;
      });

      const newOption = [...this.option];
      if (index !== -1) {
        newOption.splice(index, 1);
      } else {
        newOption.push(finalValue);
      }
      this.$emit('change', newOption);
    },
    getLabel(value) {
      if (Array.isArray(value) && this.multiple) {
        const labels = value.map((v) => (this.valueLabel ? v[this.valueLabel] : v));
        return labels.join(', ');
      } if (this.valueLabel) {
        return value[this.valueLabel];
      }
      return value;
    },
    toggleOpen() {
      if (!this.disabled) {
        this.open = !this.open;
      }
      this.autocompleteLabel = '';
      this.filteredList = this.options;
    },
    updateAutoCompleteValue(value) {
      this.autocompleteLabel = value;
    },
  },
};
</script>

<template>
  <div
    v-clickOut="hide"
    class="cxm-Select-SelectContainer"
    :class="getClasses"
    tabindex="0"
  >
    <InputComponent
      v-bind="$props"
      :id="id"
      :value="labelShowed"
      :placeholder="placeholder"
      :info-message="infoMessage"
      :label="label"
      type="text"
      autocomplete="off"
      @click="toggleOpen"
    >
      <template #rightIcons>
        <Icon
          name="DropDown"
          :handle-click="toggleOpen"
          class="cxm-Select-IconDropdown"
        />
      </template>
      <template
        v-if="showLeftIcon"
        #leftIcons
      >
        <Icon
          :name="iconLeft"
          size="20"
          class="cxm-SelectIconLeft"
        />
      </template>
    </InputComponent>
    <div
      v-show="open"
      :class="['cxm-Select-Items', dropdownClass]"
    >
      <AutoCompleteInput
        :open="open"
        :filter-backend="backendFilter"
        :options="options"
        :disabled="disabled"
        :placeholder="paceholderAutocompleteText"
        :value="autocompleteLabel"
        @input="updateAutoCompleteValue"
      />
      <div class="cxm-Select-Scroll">
        <div
          v-for="(option, i) of filteredList"
          :key="i"
          class="cxm-Select-Item"
          :class="{ disabled: option.disabled, IconSlot: hasIconSlot }"
          :title="option.tooltipText ? null : getLabel(option)"
          @click.prevent="selectOption(option)"
        >
          <slot
            name="option"
            :option="option"
          />
          <Tooltip
            :text="option.tooltipText"
            position="middle right"
            style="width: 100%"
          >
            <div class="cxm-Select-Span">
              <span v-if="!hasOptionSlot">{{ getLabel(option) }}</span>
            </div>
          </Tooltip>
          <slot
            name="iconRight"
            :option="option"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cxm-Select-SelectContainer {
  position: relative;
  width: 100%;
  text-align: left;
  outline: none;

  &.-display {
    display: flex;
    flex-direction: column;
    width: 240px;
  }

  .cxm-Select-Items {
    color: var(--track-color-bluegray-70);
    font-size: var(--track-font-size-xs);
    border-radius: 0px 0px 2px 2px;
    position: absolute;
    background-color: var(--track-color-white);
    left: 0;
    right: 0;
    z-index: 100;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14),
      0 3px 1px -2px rgba(0, 0, 0, 0.12);
  }

  .cxm-Select-Scroll {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 200px;
  }

  .cxm-Select-Item {
    padding-left: var(--track-spacing-sm);
    cursor: pointer;
    user-select: none;
    line-height: 40px;
    word-break: break-all;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
    &:hover {
      background-color: var(--track-color-bluegray-10);
    }
    &.disabled {
      color: var(--track-color-bluegray-50);
      cursor: not-allowed;
    }
  }
  .cxm-Select-IconDropdown {
    fill: var(--track-color-bluegray-70);
  }
  .cxm-Select-Span {
    width: 70%;
  }
  .IconSlot {
    display: flex;
  }
  .--dropdown-top {
    bottom: 100%;
    top: auto;
  }
  .--dropdown-bottom {
    top: 100%;
    bottom: auto;
  }
  .cxm-SelectIconLeft {
    margin-bottom: var(--track-spacing-xs);
  }
}
</style>
